import type { IncomeTaxRuleSet } from "./types.js";

// income-tax ruleset for FY2025-26 (1 July 2025 – 30 June 2026).
//
// Every figure below is transcribed from a verification ledger
// (docs/research/discovery/verification/*-figures.md) with its source + the
// per-figure correction the data-critic applied. Where a ledger resolved a
// conflict, the CORRECTED current-FY value is used (e.g. Medicare single
// low-income threshold $28,011, NOT the stale 2024-25 $27,222).
//
// Never edit a shipped ruleset to "fix" a later year — author a new
// fyXXXX-XX.ts and register it. Brackets, levy thresholds, LITO/SAPTO, HELP
// bands and the part-year threshold all reprice; re-verify each FY rollover.
export const fy2025_26: IncomeTaxRuleSet = {
  engine: "income-tax",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sources: {
    residentRates:
      "https://www.ato.gov.au/tax-rates-and-codes/tax-rates-australian-residents",
    foreignRates:
      "https://www.ato.gov.au/tax-rates-and-codes/tax-rates-foreign-residents",
    whmRates:
      "https://www.ato.gov.au/tax-rates-and-codes/tax-rates-working-holiday-makers",
    medicareLevy:
      "https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy",
    medicareReduction:
      "https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy/medicare-levy-reduction/medicare-levy-reduction-for-low-income-earners",
    lito: "https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/tax-offsets/low-income-tax-offset",
    sapto:
      "https://atotaxrates.info/tax-offset/senior-australians-tax-offset/",
    partYearThreshold:
      "https://www.ato.gov.au/individuals-and-families/coming-to-australia-or-going-overseas/coming-to-australia/tax-free-threshold-for-newcomers-to-australia",
    helpThresholds:
      "https://www.ato.gov.au/tax-rates-and-codes/study-and-training-support-loans-rates-and-repayment-thresholds",
  },
  sourcesVerifiedAt: "2026-06-22",

  // Resident brackets, post-Stage-3 (take-home-pay ledger #1, CITED).
  //   $0–18,200      : nil
  //   $18,201–45,000 : 16c per $1 over $18,200
  //   $45,001–135,000: 30c per $1 over $45,000
  //   $135,001–190,000: 37c per $1 over $135,000
  //   $190,001+      : 45c per $1 over $190,000
  taxFreeThreshold: 18_200,
  residentBrackets: [
    { from: 0, to: 18_201, rate: 0 },
    { from: 18_201, to: 45_001, rate: 0.16 },
    { from: 45_001, to: 135_001, rate: 0.3 },
    { from: 135_001, to: 190_001, rate: 0.37 },
    { from: 190_001, to: null, rate: 0.45 },
  ],

  // Foreign-resident brackets (nonresident ledger #1/#4, CITED). 30% from the
  // first dollar to $135,000 (NOT the stale pre-Stage-3 32.5%); no tax-free
  // threshold; PwC cumulatives $40,500 @ $135k, $60,850 @ $190k.
  foreignBrackets: [
    { from: 0, to: 135_001, rate: 0.3 },
    { from: 135_001, to: 190_001, rate: 0.37 },
    { from: 190_001, to: null, rate: 0.45 },
  ],

  // Working-holiday-maker schedule (whm ledger #1, CITED; == 2024-25).
  //   $0–45,000   : 15c per $1 (from the first dollar — no threshold)
  //   $45,001–135,000 : $6,750 + 30c per $1 over $45,000
  //   $135,001–190,000: $33,750 + 37c per $1 over $135,000
  //   $190,001+   : $54,100 + 45c per $1 over $190,000
  // Encoded as marginal brackets; the cumulatives $6,750/$33,750/$54,100 are
  // the arithmetic consequence and are checked in the tests.
  whmBrackets: [
    { from: 0, to: 45_001, rate: 0.15 },
    { from: 45_001, to: 135_001, rate: 0.3 },
    { from: 135_001, to: 190_001, rate: 0.37 },
    { from: 190_001, to: null, rate: 0.45 },
  ],

  // Medicare levy + low-income reduction (medicare-levy ledger, CITED rate /
  // mechanic; ledger #7 RESOLUTION: current-FY single lower = $28,011, not the
  // stale $27,222). Upper = lower × 1.25 for a 2% rate + 10c shade-in.
  // Family + SAPTO + per-child uplifts are the 2025-26 set from the ledger's
  // resolved Fig 7 (AGENT_VERIFIED — two independent secondaries agree;
  // upgrade to CITED on a browser open of the ATO page per the ledger's P4 note).
  medicareLevy: {
    rate: 0.02,
    reductionRate: 0.1,
    singleLowerThreshold: 28_011,
    singleUpperThreshold: 35_013, // 28,011 × 1.25 = 35,013.75 → 35,013
    familyLowerThreshold: 47_238,
    familyUpperThreshold: 59_047, // 47,238 × 1.25 = 59,047.5 → 59,047
    familyChildLowerIncrement: 4_338,
    familyChildUpperIncrement: 5_423,
    saptoSingleLowerThreshold: 44_268,
    saptoSingleUpperThreshold: 55_335,
  },

  // LITO (lito ledger #1–#3, CITED, verbatim ATO tapers).
  //   ≤$37,500      : $700
  //   $37,501–45,000: $700 − 5c per $1 over $37,500 (→ $325 at $45,000)
  //   $45,001–66,667: $325 − 1.5c per $1 over $45,000 (→ $0 at $66,667)
  lito: {
    max: 700,
    taperStart: 37_500,
    taper1Rate: 0.05,
    taper2Start: 45_000,
    taper2Rate: 0.015,
    cutOut: 66_667,
  },

  // SAPTO single path (sapto ledger #1/#3/#5, CITED; conflict resolved in
  // favour of the 2025-26 $34,919 / $52,759 pair — NOT the stale 2023-24
  // $33,532 / $50,119). Max $2,230 single, taper 12.5c/$1 over shade-out.
  sapto: {
    maxSingle: 2_230,
    shadeOutSingle: 34_919,
    cutOutSingle: 52_759,
    taperRate: 0.125,
  },

  // Part-year resident pro-rated tax-free threshold (part-year ledger #1/#2,
  // CITED). threshold = $13,464 + $4,736 × residentMonths/12. ATO worked
  // example: resident from 17 Apr → 3 months → $14,648.
  partYearThreshold: {
    base: 13_464,
    proRated: 4_736,
  },

  // HELP/STSL marginal bands (take-home-pay ledger #5 + help-not-withheld
  // ledger #3, CITED). Min $67,000; 15c/$1 to $125,000; $8,700 + 17c/$1 to
  // $179,285; 10% of WHOLE repayment income above $179,286. Mirrors AU-06.
  helpMinThreshold: 67_000,
  helpBands: [
    { from: 0, to: 67_001, baseAmount: 0, rate: 0 },
    { from: 67_001, to: 125_001, baseAmount: 0, rate: 0.15 },
    { from: 125_001, to: 179_286, baseAmount: 8_700, rate: 0.17 },
    { from: 179_286, to: null, baseAmount: 0, rate: 0.1, wholeIncome: true },
  ],
};
